import { useEffect, useMemo, useState } from "react";
import { StyleSheet, Text, View } from "react-native";

import {
  AppText,
  Button,
  ButtonText,
  Card,
  EmptyState,
  FieldLabel,
  MetricCard,
  ProtectedScreen,
  SearchableSelect,
  Stack,
  TextField,
} from "~/components/ui";
import {
  usePrimaryWorkspace,
  useWorkspaceClients,
  useWorkspaceProjects,
  useWorkspaceTasks,
  useWorkspaceTimeEntries,
  useWorkspaceUserRates,
} from "~/hooks/workspace";
import { useAuth } from "~/hooks/useAuth";
import { useWorkspaceReadOnly } from "~/hooks/useWorkspaceReadOnly";
import {
  fonts,
  radii,
  spacing,
  palette,
  useThemedStyles,
  type ThemeColors,
} from "~/styles/theme";
import { resolveRatePresetCents } from "~/utils/billing-rate";

function formatDuration(seconds: number) {
  const safeSeconds = Math.max(0, seconds);
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const remainingSeconds = safeSeconds % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(remainingSeconds).padStart(2, "0")}`;
}

function isToday(isoDate: string) {
  const value = new Date(isoDate);
  const now = new Date();
  return (
    value.getFullYear() === now.getFullYear() &&
    value.getMonth() === now.getMonth() &&
    value.getDate() === now.getDate()
  );
}

function dayLabel(isoDate: string) {
  if (isToday(isoDate)) return "Today";
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const value = new Date(isoDate);
  if (
    value.getFullYear() === yesterday.getFullYear() &&
    value.getMonth() === yesterday.getMonth() &&
    value.getDate() === yesterday.getDate()
  ) {
    return "Yesterday";
  }
  return value.toLocaleDateString([], {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

function billableValue(entry: {
  billable: boolean;
  duration_seconds: number;
  hourly_rate_cents: number | null;
}) {
  if (!entry.billable || !entry.hourly_rate_cents) return 0;
  return Math.round((entry.duration_seconds / 3600) * entry.hourly_rate_cents);
}

function formatMoney(cents: number) {
  return `$${(cents / 100).toFixed(2)}`;
}

export default function TimerScreen() {
  const stylesheet = useThemedStyles(makeStyles);
  const { user } = useAuth();
  const workspace = usePrimaryWorkspace();
  const { readOnly } = useWorkspaceReadOnly(workspace.data?.id);
  const clients = useWorkspaceClients(workspace.data?.id);
  const projects = useWorkspaceProjects(workspace.data?.id);
  const tasks = useWorkspaceTasks(workspace.data?.id);
  const timeEntries = useWorkspaceTimeEntries(workspace.data?.id);
  const userRates = useWorkspaceUserRates(workspace.data?.id);
  const clientRecords = clients.data ?? [];
  const projectRecords = projects.data ?? [];
  const taskRecords = tasks.data ?? [];
  const clientById = new Map(
    clientRecords.map((client) => [client.id, client]),
  );
  const projectById = new Map(
    projectRecords.map((project) => [project.id, project]),
  );
  const taskById = new Map(taskRecords.map((task) => [task.id, task]));
  const clientIdForEntry = (entry: {
    client_id: string | null;
    project_id: string | null;
  }) =>
    entry.client_id ??
    (entry.project_id
      ? (projectById.get(entry.project_id)?.client_id ?? null)
      : null);
  const clientOptions = [
    { label: "No client", value: "" },
    ...clientRecords.map((client) => ({
      label: client.name,
      value: client.id,
    })),
  ];
  const billingOptions = [
    { label: "Billable", value: "true" },
    { label: "Non-billable", value: "false" },
  ];
  const [billable, setBillable] = useState("true");
  const [clientId, setClientId] = useState("");
  const [description, setDescription] = useState("");
  const [hourlyRate, setHourlyRate] = useState("");
  const [projectId, setProjectId] = useState("");
  const [taskId, setTaskId] = useState("");
  const [tags, setTags] = useState("");
  const [now, setNow] = useState(() => new Date());
  const filteredProjectOptions = [
    { label: "No project", value: "" },
    ...projectRecords
      .filter((project) => !clientId || project.client_id === clientId)
      .map((project) => ({ label: project.name, value: project.id })),
  ];
  const filteredTaskOptions = [
    { label: "No task", value: "" },
    ...taskRecords
      .filter((task) => {
        if (projectId) return task.project_id === projectId;
        if (!clientId) return true;
        const project = task.project_id
          ? projectById.get(task.project_id)
          : null;
        return project?.client_id === clientId;
      })
      .map((task) => ({ label: task.name, value: task.id })),
  ];
  const selectedProject = projectId ? projectById.get(projectId) : null;
  const ratePresetCents = resolveRatePresetCents(
    userRates.rates.filter((rate) => rate.user_id === user?.id),
    { clientId, projectId },
  );
  const suggestedHourlyRateCents =
    ratePresetCents ??
    selectedProject?.hourly_rate_cents ??
    workspace.data?.default_hourly_rate_cents ??
    0;
  const suggestedHourlyRate = suggestedHourlyRateCents
    ? String(suggestedHourlyRateCents / 100)
    : "";
  const entries = timeEntries.data ?? [];
  const activeTimer =
    entries.find(
      (entry) =>
        entry.user_id === user?.id &&
        entry.source === "timer" &&
        entry.ended_at === null,
    ) ?? null;
  const startedAt = activeTimer ? new Date(activeTimer.started_at) : null;

  useEffect(() => {
    if (!startedAt) return;
    const interval = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(interval);
  }, [startedAt]);

  useEffect(() => {
    if (!startedAt) setHourlyRate(suggestedHourlyRate);
  }, [clientId, projectId, startedAt, suggestedHourlyRate]);

  useEffect(() => {
    if (!activeTimer) return;
    setBillable(activeTimer.billable ? "true" : "false");
    setClientId(activeTimer.client_id ?? "");
    setDescription(activeTimer.description ?? "");
    setHourlyRate(
      activeTimer.hourly_rate_cents
        ? String(activeTimer.hourly_rate_cents / 100)
        : "",
    );
    setProjectId(activeTimer.project_id ?? "");
    setTaskId(activeTimer.task_id ?? "");
    setTags((activeTimer.tags ?? []).map((tag) => tag.name).join(", "));
  }, [activeTimer]);

  const todayEntries = useMemo(
    () =>
      entries.filter((entry) => entry.ended_at && isToday(entry.started_at)),
    [entries],
  );
  const recentEntryGroups = useMemo(() => {
    const groups = new Map<string, typeof entries>();
    entries
      .filter((entry) => entry.ended_at)
      .sort((left, right) => right.started_at.localeCompare(left.started_at))
      .slice(0, 20)
      .forEach((entry) => {
        const key = entry.started_at.slice(0, 10);
        groups.set(key, [...(groups.get(key) ?? []), entry]);
      });
    return [...groups.entries()];
  }, [entries]);
  const todaySeconds = todayEntries.reduce(
    (total, entry) => total + entry.duration_seconds,
    0,
  );
  const todayValue = todayEntries.reduce(
    (total, entry) => total + billableValue(entry),
    0,
  );
  const elapsedSeconds = startedAt
    ? Math.max(0, Math.floor((now.getTime() - startedAt.getTime()) / 1000))
    : 0;
  const error =
    workspace.error ??
    clients.error ??
    projects.error ??
    tasks.error ??
    userRates.error ??
    timeEntries.error ??
    timeEntries.startTimerEntry.error ??
    timeEntries.stopTimerEntry.error ??
    timeEntries.discardTimerEntry.error;
  const errorMessage = error instanceof Error ? error.message : "";
  const saving =
    timeEntries.startTimerEntry.isPending ||
    timeEntries.stopTimerEntry.isPending ||
    timeEntries.discardTimerEntry.isPending;

  function selectClient(value: string) {
    setClientId(value);
    setProjectId("");
    setTaskId("");
  }

  function selectProject(value: string) {
    setProjectId(value);
    setTaskId("");
    const project = value ? projectById.get(value) : null;
    if (project?.client_id) setClientId(project.client_id);
  }

  function selectTask(value: string) {
    setTaskId(value);
    const task = value ? taskById.get(value) : null;
    if (!task?.project_id) return;
    setProjectId(task.project_id);
    const project = projectById.get(task.project_id);
    if (project?.client_id) setClientId(project.client_id);
  }

  async function stopTimer() {
    if (!activeTimer || saving) return;
    await timeEntries.stopTimerEntry.mutateAsync({
      id: activeTimer.id,
      endedAt: new Date(),
    });
    resetTimerForm();
  }

  async function startTimer() {
    if (!description.trim() || saving) return;
    await timeEntries.startTimerEntry.mutateAsync({
      billable,
      clientId,
      description: description.trim(),
      hourlyRate,
      projectId,
      taskId,
      tags,
    });
    setNow(new Date());
  }

  async function discardTimer() {
    if (!activeTimer || saving) return;
    await timeEntries.discardTimerEntry.mutateAsync(activeTimer.id);
    resetTimerForm();
  }

  function resetTimerForm() {
    setBillable("true");
    setClientId("");
    setDescription("");
    setHourlyRate("");
    setProjectId("");
    setTaskId("");
    setTags("");
  }

  function reuseEntryDetails(entry: (typeof entries)[number]) {
    setBillable(entry.billable ? "true" : "false");
    setClientId(entry.client_id ?? "");
    setDescription(entry.description ?? "");
    setHourlyRate(
      entry.hourly_rate_cents ? String(entry.hourly_rate_cents / 100) : "",
    );
    setProjectId(entry.project_id ?? "");
    setTaskId(entry.task_id ?? "");
  }

  return (
    <ProtectedScreen
      title="Timer"
      subtitle="Track billable work and save it straight into your MintStint timesheet."
    >
      <View style={stylesheet.metricsGrid}>
        <View style={stylesheet.metric}>
          <MetricCard
            label="Today"
            value={formatDuration(todaySeconds)}
            accent="mint"
          />
        </View>
        <View style={stylesheet.metric}>
          <MetricCard label="Billable value" value={formatMoney(todayValue)} />
        </View>
      </View>

      <Card gap="lg">
        <View style={stylesheet.timerChrome}>
          <View style={stylesheet.timerFace}>
            <AppText size="xs" weight="semibold" tone="inverse">
              {startedAt ? "Timer running" : "What are you working on?"}
            </AppText>
            <Text style={stylesheet.timerDigits}>
              {formatDuration(elapsedSeconds)}
            </Text>
          </View>
          <View style={stylesheet.chromeActions}>
            {readOnly ? (
              <AppText size="sm" tone="inverse">
                Accountant seats can view tracked time but cannot run the timer.
              </AppText>
            ) : startedAt ? (
              <>
                <Button
                  iconName="square"
                  disabled={saving}
                  onPress={() => void stopTimer()}
                >
                  <ButtonText>
                    {saving ? "Saving..." : "Stop and save"}
                  </ButtonText>
                </Button>
                <Button
                  variant="outline"
                  disabled={saving}
                  onPress={() => void discardTimer()}
                >
                  <ButtonText>
                    {timeEntries.discardTimerEntry.isPending
                      ? "Discarding..."
                      : "Discard"}
                  </ButtonText>
                </Button>
              </>
            ) : (
              <Button
                iconName="play"
                disabled={!description.trim() || workspace.isLoading || saving}
                onPress={() => void startTimer()}
              >
                <ButtonText>
                  {timeEntries.startTimerEntry.isPending
                    ? "Starting..."
                    : "Start timer"}
                </ButtonText>
              </Button>
            )}
          </View>
        </View>

        <View style={stylesheet.formGrid}>
          <View style={stylesheet.field}>
            <FieldLabel>Description</FieldLabel>
            <TextField
              accessibilityLabel="Description"
              value={description}
              placeholder="Client call, writing, coding, admin..."
              editable={!activeTimer && !readOnly}
              muted={Boolean(activeTimer) || readOnly}
              onChangeText={setDescription}
            />
          </View>
          <View style={stylesheet.selectField}>
            <FieldLabel>Client</FieldLabel>
            <SearchableSelect
              title="Choose client"
              value={clientId}
              options={clientOptions}
              placeholder="No client"
              disabled={Boolean(activeTimer) || readOnly}
              onValueChange={selectClient}
            />
          </View>
          <View style={stylesheet.selectField}>
            <FieldLabel>Project</FieldLabel>
            <SearchableSelect
              title="Choose project"
              value={projectId}
              options={filteredProjectOptions}
              placeholder="No project"
              disabled={Boolean(activeTimer) || readOnly}
              onValueChange={selectProject}
            />
          </View>
          <View style={stylesheet.selectField}>
            <FieldLabel>Task</FieldLabel>
            <SearchableSelect
              title="Choose task"
              value={taskId}
              options={filteredTaskOptions}
              placeholder="No task"
              disabled={Boolean(activeTimer) || readOnly}
              onValueChange={selectTask}
            />
          </View>
          <View style={stylesheet.rateField}>
            <FieldLabel>Billing</FieldLabel>
            <SearchableSelect
              title="Choose billing state"
              value={billable}
              options={billingOptions}
              placeholder="Billable"
              disabled={Boolean(activeTimer) || readOnly}
              onValueChange={setBillable}
            />
          </View>
          <View style={stylesheet.rateField}>
            <FieldLabel>Hourly rate</FieldLabel>
            <TextField
              accessibilityLabel="Hourly rate"
              value={hourlyRate}
              placeholder="120"
              keyboardType="numeric"
              editable={billable !== "false" && !activeTimer && !readOnly}
              muted={billable === "false" || Boolean(activeTimer) || readOnly}
              onChangeText={setHourlyRate}
            />
          </View>
          <View style={stylesheet.selectField}>
            <FieldLabel>Tags (comma-separated)</FieldLabel>
            <TextField
              accessibilityLabel="Time entry tags"
              value={tags}
              placeholder="discovery, workshop"
              editable={!activeTimer && !readOnly}
              muted={Boolean(activeTimer) || readOnly}
              onChangeText={setTags}
            />
          </View>
        </View>

        {errorMessage ? (
          <AppText size="xs" tone="danger">
            {errorMessage}
          </AppText>
        ) : null}
      </Card>

      <Card gap="md">
        <Stack gap="xs">
          <AppText weight="semibold">Recent timer entries</AppText>
          <AppText size="xs" tone="muted">
            Reuse a recent entry, then start timer when ready.
          </AppText>
        </Stack>
        {recentEntryGroups.length === 0 ? (
          <EmptyState
            title="No timer entries saved today"
            description="Timer entries appear here after you stop and save them."
          />
        ) : (
          <Stack gap="md">
            {recentEntryGroups.map(([date, dayEntries]) => (
              <Stack key={date} gap="sm">
                <AppText size="xs" weight="semibold">
                  {dayLabel(`${date}T12:00:00`)}
                </AppText>
                {dayEntries.map((entry) => (
                  <View key={entry.id} style={stylesheet.entryRow}>
                    <View style={stylesheet.entryMain}>
                      <AppText weight="semibold">
                        {entry.description ?? "Timed work"}
                      </AppText>
                      <AppText size="xs" tone="muted">
                        {new Date(entry.started_at).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </AppText>
                      <AppText size="xs" tone="muted">
                        {clientIdForEntry(entry)
                          ? (clientById.get(clientIdForEntry(entry) ?? "")
                              ?.name ?? "Unknown client")
                          : "No client"}
                        {" - "}
                        {entry.project_id
                          ? (projectById.get(entry.project_id)?.name ??
                            "Unknown project")
                          : "No project"}
                        {" - "}
                        {entry.task_id
                          ? (taskById.get(entry.task_id)?.name ??
                            "Unknown task")
                          : "No task"}
                      </AppText>
                      <AppText size="xs" tone="muted">
                        {entry.billable ? "Billable" : "Non-billable"}
                      </AppText>
                    </View>
                    <View style={stylesheet.entryActions}>
                      <View style={stylesheet.entryPill}>
                        <AppText size="xs" tone="muted">
                          {formatDuration(entry.duration_seconds)}
                        </AppText>
                      </View>
                      <Button
                        compact
                        variant="outline"
                        disabled={Boolean(activeTimer)}
                        onPress={() => reuseEntryDetails(entry)}
                      >
                        <ButtonText>Use again</ButtonText>
                      </Button>
                    </View>
                  </View>
                ))}
              </Stack>
            ))}
          </Stack>
        )}
      </Card>
    </ProtectedScreen>
  );
}

const makeStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    metricsGrid: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: spacing.m,
    },
    metric: {
      minWidth: 180,
      flex: 1,
    },
    timerChrome: {
      flexDirection: "row",
      flexWrap: "wrap",
      alignItems: "stretch",
      gap: spacing.m,
      padding: spacing.m,
      borderWidth: 1,
      borderColor: palette.brandForest,
      borderRadius: radii.l,
      backgroundColor: palette.brandForest,
    },
    timerFace: {
      minHeight: 120,
      minWidth: 220,
      flex: 1,
      alignItems: "flex-start",
      justifyContent: "center",
      gap: spacing.s,
    },
    timerDigits: {
      color: colors.primary,
      fontFamily: fonts.bold,
      fontSize: 40,
      lineHeight: 44,
    },
    chromeActions: {
      minWidth: 180,
      justifyContent: "center",
      gap: spacing.s,
    },
    formGrid: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: spacing.m,
    },
    field: {
      minWidth: 240,
      flex: 1,
      gap: spacing.xs,
    },
    rateField: {
      width: 180,
      gap: spacing.xs,
    },
    selectField: {
      minWidth: 180,
      flex: 1,
      gap: spacing.xs,
    },
    actions: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: spacing.s,
    },
    entryRow: {
      flexDirection: "row",
      flexWrap: "wrap",
      alignItems: "center",
      justifyContent: "space-between",
      gap: spacing.m,
      padding: spacing.m,
      borderWidth: 1,
      borderColor: colors.hairline,
      borderRadius: radii.m,
    },
    entryMain: {
      flex: 1,
      minWidth: 180,
      gap: spacing.xs,
    },
    entryActions: {
      alignItems: "flex-end",
      gap: spacing.s,
    },
    entryPill: {
      minHeight: 30,
      justifyContent: "center",
      paddingHorizontal: spacing.s,
      borderWidth: 1,
      borderColor: colors.hairline,
      borderRadius: radii.control,
      backgroundColor: colors.surfaceSunken,
    },
  });
